import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Plural, Trans } from "@lingui/react/macro";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  alpha,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  lighten,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  addDays,
  addMonths,
  differenceInDays,
  format,
  isSameDay,
  parseISO,
  startOfMonth,
} from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import Block from "@/components/Block";
import Counter from "@/components/Counter";
import DateRangeCalendar from "@/components/DateRangeCalendar";
import DateRangeCalendarSkeleton from "@/components/DateRangeCalendarSkeleton";
import TextButton from "@/components/TextButton";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { useAccommodationDayInfo, usePriceCheck } from "@/hooks";
import { primaryActionSx } from "@/theme/mui-theme";
import {
  computeStayLengthAvailability,
  findClosestAvailableNights,
  getValidArrivalDays,
} from "@/utils/alternativeDates";
import { analytics } from "@/utils/analytics";
import { formatDateRange } from "@/utils/booking";

interface AlternativeDatesCalendarProps {
  onSearch: (dateFrom: Date, dateUntil: Date) => void;
  accoId: number;
}

export default function AlternativeDatesCalendar({
  onSearch,
  accoId,
}: AlternativeDatesCalendarProps) {
  const { state } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const { loading, data: dayInfoData, fetchDayInfo } = useAccommodationDayInfo();
  const [dayInfoError, setDayInfoError] = useState(false);
  const { price: localPrice, loading: priceLoading, checkPrice, formatPrice } = usePriceCheck();
  const { i18n, _ } = useLingui();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));

  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [stayDaySelected, setStayDaySelected] = useState<Date | null>(null);
  const [selectedAccoId, setSelectedAccoId] = useState(accoId);
  const [selectedNights, setSelectedNights] = useState(() =>
    differenceInDays(state.dateUntil!, state.dateFrom!),
  );
  const [nightsAutoAdjustedFrom, setNightsAutoAdjustedFrom] = useState<number | null>(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  // Ref so the auto-adjust effect only fires when availability data changes, not on every manual nights change
  const selectedNightsRef = useRef(selectedNights);
  selectedNightsRef.current = selectedNights;
  // Tracks the last date we sent a price request for, to deduplicate the double
  // onSelect call that DateRangeCalendar emits per click (DayPicker + handleDayClick)
  const lastFetchedFrom = useRef<Date | null>(null);

  const selectedCampsiteType = state.selectedCampsiteType;

  const availableAccos = useMemo(
    () =>
      campgroundData.campground.campsites.filter((c) => c.campsite_type === selectedCampsiteType),
    [campgroundData, selectedCampsiteType],
  );

  // Fetch day info when the accommodation changes
  useEffect(() => {
    if (!selectedAccoId) return;

    const today = new Date();
    const dateFrom = format(today, "yyyy-MM-dd");
    const dateTo = format(addMonths(startOfMonth(today), 12), "yyyy-MM-dd");

    setDayInfoError(false);
    fetchDayInfo({ dateFrom, dateTo, accoId: selectedAccoId }).catch(() => setDayInfoError(true));
    setDateRange(undefined);
    setStayDaySelected(null);
  }, [selectedAccoId, fetchDayInfo]);

  // Pre-compute valid arrival days: days where the full N-night stay is bookable
  const validArrivalDays = useMemo(() => {
    if (!dayInfoData?.day_list || selectedNights <= 0) return [];

    return getValidArrivalDays(dayInfoData.day_list, selectedNights);
  }, [dayInfoData, selectedNights]);

  // Highlight all dates that fall within any valid stay period but aren't arrivals themselves.
  // E.g. if arrivals are Mar 1 & Mar 2 for 7 nights, validStayDays = Mar 2–8 ∪ Mar 3–9 minus arrivals.
  const validStayDays = useMemo(() => {
    if (selectedNights <= 0) return [];
    const arrivalSet = new Set(validArrivalDays.map((d) => format(d, "yyyy-MM-dd")));
    const staySet = new Set<string>();
    for (const arrivalDate of validArrivalDays) {
      for (let i = 1; i <= selectedNights; i++) {
        const key = format(addDays(arrivalDate, i), "yyyy-MM-dd");
        if (!arrivalSet.has(key)) staySet.add(key);
      }
    }
    return Array.from(staySet).map((s) => parseISO(s));
  }, [validArrivalDays, selectedNights]);

  const firstAvailableMonth = useMemo(
    () => (validArrivalDays.length > 0 ? startOfMonth(validArrivalDays[0]!) : undefined),
    [validArrivalDays],
  );

  const defaultMonthNearSearch = useMemo(() => {
    if (!state.dateFrom || validArrivalDays.length === 0) return firstAvailableMonth;
    const closest = validArrivalDays.reduce((a, b) =>
      Math.abs(differenceInDays(a, state.dateFrom!)) <=
      Math.abs(differenceInDays(b, state.dateFrom!))
        ? a
        : b,
    );
    return startOfMonth(closest);
  }, [state.dateFrom, validArrivalDays, firstAvailableMonth]);

  const stayLengthAvailability = useMemo(
    () => (dayInfoData?.day_list ? computeStayLengthAvailability(dayInfoData.day_list) : {}),
    [dayInfoData],
  );

  // Auto-adjust selectedNights to the closest available option when data loads
  useEffect(() => {
    if (Object.keys(stayLengthAvailability).length === 0) return;
    const nights = selectedNightsRef.current;
    const candidate = findClosestAvailableNights(stayLengthAvailability, nights);
    if (candidate === null) return;
    setNightsAutoAdjustedFrom(nights);
    setSelectedNights(candidate);
    setDateRange(undefined);
  }, [stayLengthAvailability]);

  const getDisabledReason = useCallback(
    (date: Date): string | null => {
      if (validArrivalDays.some((d) => isSameDay(d, date))) return null;
      if (validStayDays.some((d) => isSameDay(d, date))) {
        return isMobile ? null : _(msg`Arrivals not allowed on this day`);
      }
      return null;
    },
    [validArrivalDays, validStayDays, isMobile, _],
  );

  const handleAccoChange = useCallback((newId: number) => {
    setSelectedAccoId(newId);
    setDateRange(undefined);
    setStayDaySelected(null);
    lastFetchedFrom.current = null;
  }, []);

  const handleNightsChange = useCallback((n: number) => {
    setSelectedNights(n);
    setNightsAutoAdjustedFrom(null);
    setDateRange(undefined);
    setStayDaySelected(null);
    lastFetchedFrom.current = null;
  }, []);

  const handleSelect = useCallback(
    (range: DateRange | undefined) => {
      if (!range?.from) {
        setDateRange(undefined);
        lastFetchedFrom.current = null;
        return;
      }
      // Handles both { from, to: undefined } (DayPicker) and { from, to: from }
      // (DateRangeCalendar.handleDayClick re-selection) — both compute the same departure
      const newRange = { from: range.from, to: addDays(range.from, selectedNights) };
      setDateRange(newRange);
      // Call checkPrice synchronously here so React batches price=null + loading=true
      // together with the dateRange update — prevents stale price from flashing.
      // Guard against the double-fire: skip if we already fetched for this exact date.
      if (lastFetchedFrom.current && isSameDay(range.from, lastFetchedFrom.current)) return;
      lastFetchedFrom.current = range.from;
      checkPrice({
        dateFrom: newRange.from,
        dateUntil: newRange.to,
        campsiteId: selectedAccoId,
        persons: state.selectedPersons,
      }).catch(() => {});
    },
    [selectedNights, checkPrice, selectedAccoId, state.selectedPersons],
  );

  const unavailableModifier = useMemo(
    () => (date: Date) =>
      !validArrivalDays.some((d) => isSameDay(d, date)) &&
      !validStayDays.some((d) => isSameDay(d, date)),
    [validArrivalDays, validStayDays],
  );

  const handleCalendarSelect = useCallback(
    (range: DateRange | undefined) => {
      if (!range?.from) return;
      if (validArrivalDays.some((d) => isSameDay(d, range.from!))) {
        setStayDaySelected(null);
        handleSelect(range);
      } else if (isMobile && validStayDays.some((d) => isSameDay(d, range.from!))) {
        setDateRange(undefined);
        setStayDaySelected(range.from);
      }
    },
    [handleSelect, validArrivalDays, validStayDays, isMobile],
  );

  const handleSearch = useCallback(() => {
    if (dateRange?.from && dateRange?.to) {
      analytics.capture("alternative_dates_selected", {
        accommodation_name: availableAccos.find((a) => a.id === selectedAccoId)?.name,
        accommodation_type: selectedCampsiteType,
        date_from: format(dateRange.from, "yyyy-MM-dd"),
        date_until: format(dateRange.to, "yyyy-MM-dd"),
        nights: selectedNights,
        source: "calendar",
      });
      onSearch(dateRange.from, dateRange.to);
    }
  }, [dateRange, onSearch, availableAccos, selectedAccoId, selectedCampsiteType, selectedNights]);

  const legendItems = (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            background: alpha(theme.palette.success.main, 0.15),
            width: "12px",
            height: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              bgcolor: "success.dark",
            }}
          />
        </Box>
        <Typography variant="body2">Arrival available</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{ background: alpha(theme.palette.success.main, 0.1), width: "12px", height: "12px" }}
        ></Box>
        <Typography variant="body2">Stay only</Typography>
      </Stack>
    </Stack>
  );

  const helpButton = (
    <Tooltip title={<Trans>Why is a specific date unavailable for booking?</Trans>}>
      <IconButton size="small" onClick={() => setHelpDialogOpen(true)}>
        <HelpOutlineIcon fontSize="small" sx={{ color: "text.accent" }} />
      </IconButton>
    </Tooltip>
  );

  return (
    <Stack spacing={1.5} sx={{ flex: { xs: 1, sm: "initial" }, minHeight: { xs: 0 } }}>
      {/* Title row */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={{ xs: "flex-start", sm: "center" }}
        flexWrap="wrap"
        gap={1}
        pt={{ xs: 1.5, sm: 0 }}
        pl={{ xs: 2, sm: 0 }}
        pr={{ xs: 6, sm: 0 }}
        sx={{ flexShrink: 0 }}
      >
        {loading ? (
          <Skeleton variant="text" width={280} height={36} />
        ) : (
          <Typography variant="h3" fontWeight={600} my={1}>
            <Trans>
              Alternative arrival dates for {selectedNights}{" "}
              <Plural value={selectedNights} one="night" other="nights" />
            </Trans>
          </Typography>
        )}
      </Stack>
      {/* Auto-adjust warning */}
      {nightsAutoAdjustedFrom !== null && (
        <Box px={{ xs: 2, sm: 0 }} sx={{ flexShrink: 0 }}>
          <Alert severity="warning">
            <Trans>
              No <Plural value={nightsAutoAdjustedFrom} one="# night" other="# nights" /> stay is
              available. Showing <Plural value={selectedNights} one="# night" other="# nights" />{" "}
              instead — the closest available option.
            </Trans>
          </Alert>
        </Box>
      )}
      {/* Calendar */}
      <Block
        sx={{
          borderWidth: { xs: 0, sm: 1 },
          borderRadius: { xs: 0, sm: 5 },
          p: 0,
          overflow: "hidden",
          flex: { xs: 1, sm: "initial" },
          display: { xs: "flex", sm: "block" },
          flexDirection: { xs: "column" },
          minHeight: { xs: 0, sm: "auto" },
        }}
      >
        <Stack spacing={1.5} sx={{ flexShrink: 0 }}>
          {/* Filters row */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            px={{ xs: 0, sm: 2 }}
            pt={{ xs: 0, sm: 2 }}
          >
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              width={{ xs: "100%", sm: "auto" }}
            >
              {/* Accommodation option */}
              <Select
                size="small"
                value={selectedAccoId}
                onChange={(e) => handleAccoChange(Number(e.target.value))}
                renderValue={(id) => availableAccos.find((a) => a.id === id)?.name ?? ""}
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                {availableAccos.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                flexGrow={1}
                px={1}
              >
                {/* Nights count */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 4,
                    px: 2,
                    py: 0.8,
                    overflow: "hidden",
                    flex: "none",
                    "& .MuiSvgIcon-root": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  <Typography>
                    <Trans>Nights</Trans>
                  </Typography>
                  <Counter count={selectedNights} onChange={handleNightsChange} min={1} max={30} />
                </Stack>
                {/* Reset dates button (mobile only) */}
                <TextButton
                  onClick={() => setDateRange(undefined)}
                  disabled={!dateRange}
                  sx={{
                    display: { xs: "block", sm: "none" },
                    ml: "auto",
                    fontSize: "body2.fontSize",
                  }}
                >
                  <Trans>Reset dates</Trans>
                </TextButton>
              </Stack>

              <Stack
                direction="row"
                justifyContent="space-between"
                pl={1.5}
                pr={0.5}
                py={0.5}
                sx={{
                  width: "100%",
                  borderTop: "1px solid",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  display: { xs: "flex", sm: "none" },
                }}
              >
                {legendItems}
                {helpButton}
              </Stack>
            </Stack>

            {/* Help button */}
            <Box sx={{ display: { xs: "none", sm: "block" } }}>{helpButton}</Box>
          </Stack>
          <Divider sx={{ display: { xs: "none", sm: "block" } }} />
        </Stack>
        <Box sx={{ flex: { xs: 1 }, overflowY: { xs: "auto" }, minHeight: { xs: 0 } }}>
          {loading ? (
            <DateRangeCalendarSkeleton />
          ) : dayInfoError ? (
            <Box p={2}>
              <Alert severity="error">
                <Trans>Could not load availability data. Please try again.</Trans>
              </Alert>
            </Box>
          ) : (
            <>
              <DateRangeCalendar
                key={selectedNights}
                dateRange={dateRange}
                onSelect={handleCalendarSelect}
                defaultMonth={defaultMonthNearSearch}
                disabled={[]}
                modifiers={{
                  unavailable: unavailableModifier,
                  validStay: validStayDays,
                  stayDaySelected: stayDaySelected ? [stayDaySelected] : [],
                }}
                modifiersClassNames={{
                  unavailable: "unavailable",
                  validStay: "validStay",
                  stayDaySelected: "stayDaySelected",
                }}
                paletteColor="success"
                sx={{
                  "& .rdp-day.rdp-disabled": {
                    backgroundColor: "white",
                    fontWeight: "normal",
                    color: lighten(theme.palette.text.disabled, 0.3),
                    textDecoration: "line-through",
                  },
                  "& .rdp-day_button": {
                    width: "28px",
                    height: "28px",
                  },
                  "& .rdp-day": {
                    padding: 0,
                    color: theme.palette.success.dark,
                    backgroundColor: alpha(theme.palette.success.main, 0.15),
                    fontWeight: "bold",
                    border: "3px solid white",
                  },
                  "& .rdp-day::after": {
                    content: '""',
                    display: "block",
                    width: "4px",
                    height: "4px",
                    borderRadius: "50%",
                    backgroundColor: theme.palette.success.dark,
                    margin: "-6px auto 0",
                  },
                  "& .rdp-day.rdp-selected::after": {
                    backgroundColor: "white",
                  },
                  "& .rdp-day.validStay": {
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    color: alpha(theme.palette.success.main, 0.3),
                    textDecoration: "none",
                  },
                  "& .rdp-day.validStay::after": {
                    content: "none",
                  },
                  "& .rdp-day.stayDaySelected": {
                    outline: `1px solid ${theme.palette.success.dark}`,
                    outlineOffset: "-2px",
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    color: alpha(theme.palette.success.main, 0.3),
                  },
                  "& .rdp-day.stayDaySelected::after": {
                    content: "none",
                  },
                  "& .rdp-day.unavailable": {
                    backgroundColor: "white",
                    color: lighten(theme.palette.text.disabled, 0.3),
                    fontWeight: "normal",
                    cursor: "default",
                    textDecoration: "line-through",
                  },
                  "& .rdp-day.unavailable::after": {
                    content: "none",
                  },
                  "& .rdp-selected": {
                    background: `${theme.palette.success.main} !important`,
                    border: "none",
                  },
                  "& .rdp-range_middle .rdp-day_button, & .hoverRangeMiddle .rdp-day_button": {
                    backgroundColor: theme.palette.success.main,
                    color: theme.palette.success.contrastText,
                    width: "100%",
                    borderRadius: "0%",
                  },
                }}
                getDayTooltip={getDisabledReason}
              />
              <Divider sx={{ display: { xs: "none", sm: "block" } }} />
              <Stack
                direction="row"
                spacing={2}
                p={2}
                justifyContent="space-between"
                sx={{ display: { xs: "none", sm: "flex" } }}
              >
                {legendItems}
                <TextButton
                  onClick={() => setDateRange(undefined)}
                  disabled={!dateRange}
                  sx={{ fontSize: "body2.fontSize", ml: 1 }}
                >
                  <Trans>Reset dates</Trans>
                </TextButton>
              </Stack>
            </>
          )}
        </Box>
      </Block>
      {/* Bottom action row */}
      <Block
        sx={{
          borderWidth: { xs: 0, sm: 1 },
          borderRadius: { xs: 0, sm: 5 },
          boxShadow: { xs: "0px -6px 6px rgba(0, 0, 0, 0.08)", sm: "none" },
          p: 1.5,
          flexShrink: 0,
          mt: { xs: "0 !important", sm: "12px !important" },
          position: "relative",
          zIndex: 1,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent={dateRange?.from && dateRange?.to ? "space-between" : "center"}
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1}
        >
          {dateRange?.from && dateRange?.to ? (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                {formatDateRange([dateRange.from, dateRange.to], i18n.locale)} (
                <Plural value={selectedNights} one="# night" other="# nights" />)
              </Typography>
              {priceLoading ? (
                <Skeleton variant="text" width={100} />
              ) : localPrice?.total ? (
                <Typography variant="h3" color="text.accent">
                  <Trans>from</Trans> {formatPrice(localPrice.total)}
                </Typography>
              ) : (
                <Typography>
                  <Trans>No price available</Trans>
                </Typography>
              )}
            </Stack>
          ) : stayDaySelected ? (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              <Trans>Arrivals not allowed on this day</Trans>
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              <Trans>Please select an arrival date</Trans>
            </Typography>
          )}
          {dateRange?.from && dateRange?.to && (
            <Button
              variant="contained"
              size="large"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={!dateRange?.from || !dateRange?.to || !localPrice}
              sx={{ ...primaryActionSx, flexShrink: 0, px: 5, width: { xs: "100%", sm: "auto" } }}
            >
              <Trans>View offers</Trans>
            </Button>
          )}
        </Stack>
      </Block>
      {/* Help dialog */}
      <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)}>
        <DialogTitle sx={{ pr: 6 }}>
          <Trans>Why is a specific date unavailable for booking?</Trans>
          <IconButton
            onClick={() => setHelpDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <List dense disablePadding sx={{ listStyleType: "disc", pl: 2 }}>
            <ListItem sx={{ display: "list-item" }} disableGutters>
              <Typography variant="body2">
                <Trans>
                  The accommodation is closed or unavailable on one or more nights of your stay
                </Trans>
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item" }} disableGutters>
              <Typography variant="body2">
                <Trans>Arrivals or departures aren't allowed on this day of the week</Trans>
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item" }} disableGutters>
              <Typography variant="body2">
                <Trans>
                  The stay length doesn't meet the minimum or maximum nights requirement
                </Trans>
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item" }} disableGutters>
              <Typography variant="body2">
                <Trans>This date is too soon — a minimum advance booking period applies</Trans>
              </Typography>
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
